<template>
  <component
    :is="valueInputComponent"
    v-model="modelValue"
    :data="valueInputData"
    :container
    :size
    :readonly
    :placeholder="t('Select value')"
    v-bind="valueInputAttrs"
    @ready="handleReady"
  >
    <template
      v-if="loading"
      #trailing
    >
      <UIPreloader />
    </template>
  </component>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeMount, ref } from 'vue'
import { useDebounceFn } from '@vueuse/core'

import { ButtonSize, DropdownListData, I18nTranslate } from '@types'
import { FilterComparison } from '../utils/enums'
import { FilterField } from '../utils/types'

import { FILTER_LAST_PERIODS } from './utils/const'

import { monthDateToName } from '@/helpers/dates'

import { UIInputAutocomplete, UIInputSelect } from '@ui/fields'
import { UIPreloader } from '@ui/core'

type Props = {
  fieldSettings?: FilterField
  comparison?: FilterComparison
  readonly?: boolean
}

const props = defineProps<Props>()

const modelValue = defineModel<any>()

defineExpose({
  start,
  init() {
    initData()
  },
})

const container = inject<string | undefined>('container', undefined)
const size = inject<ButtonSize>('size')
const t = inject<I18nTranslate>('t', (data: string) => data)

const inputRef = ref<HTMLInputElement>()
const internalListData = ref<DropdownListData[]>()

const loading = ref(false)

const isSelectField = computed(
  () =>
    props.comparison === FilterComparison.LAST || props.fieldSettings?.isMonth,
)

const valueInputComponent = computed(() =>
  isSelectField.value ? UIInputSelect : UIInputAutocomplete,
)

const dateData = computed(
  () =>
    (props.fieldSettings?.isMonth
      ? listData.value?.map(item => ({
        ...item,
        label: monthDateToName(`1999-${item.value}-01`),
      }))
      : FILTER_LAST_PERIODS.map(value => ({
        value: value,
        label: `${value} ${t('days')}`,
      }))) || [],
)

const listData = computed(
  () => props.fieldSettings?.list || internalListData.value || [],
)

const valueInputData = computed(() =>
  isSelectField.value ? dateData.value : listData.value,
)

const valueInputAttrs = computed(() =>
  isSelectField.value ? {} : { placement: 'bottom-end' },
)

const initData = useDebounceFn(async () => {
  internalListData.value = undefined
  if (!props.fieldSettings?.getter) return
  loading.value = true
  internalListData.value = await props.fieldSettings.getter()
  loading.value = false
}, 200)

async function start() {
  await initData()
  inputRef.value?.focus()
}

const handleReady = (el: HTMLInputElement) => {
  inputRef.value = el
}

onBeforeMount(initData)
</script>
